import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Star } from "lucide-react";
import { api } from "@/lib/api";

const AUTOPLAY_MS = 3500;

const STATIC_FALLBACK = [
  {
    id: "fallback-1",
    name: "Lauren M.",
    town: "Pine Hill",
    quote:
      "Isaac was professional from the first knock. My bins haven't smelled like this since the day I bought them.",
    rating: 5,
    avatar_color: "#0EA5E9",
    photo_url: "/testimonials/testimonial-1.jpg",
    photo_pending: false,
  },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

export default function TestimonialCarousel({ items }) {
  const [fetched, setFetched] = useState(null);
  const list = items || fetched;

  useEffect(() => {
    if (items) return;
    let cancelled = false;
    api
      .get("/testimonials", { params: { limit: 6 } })
      .then(({ data }) => {
        if (!cancelled) setFetched(data.items || STATIC_FALLBACK);
      })
      .catch(() => {
        if (!cancelled) setFetched(STATIC_FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  if (!list) {
    return (
      <div
        className="relative bg-white rounded-3xl border border-slate-100 shadow-lift p-8 sm:p-10 min-h-[420px] flex items-center justify-center"
        data-testid="testimonial-carousel-loading"
        aria-live="polite"
      >
        <div className="h-6 w-6 rounded-full border-2 border-brand-200 border-t-brand animate-spin" />
      </div>
    );
  }

  if (list.length === 0) return null;
  return <Carousel list={list} />;
}

function Carousel({ list }) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const count = list.length;

  const go = (n) => {
    setIndex(((n % count) + count) % count);
    setProgress(0);
    startRef.current = null;
  };

  useEffect(() => {
    if (reduced || paused || count <= 1) return;
    const tick = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const pct = Math.min(100, (elapsed / AUTOPLAY_MS) * 100);
      setProgress(pct);
      if (elapsed >= AUTOPLAY_MS) {
        setIndex((i) => (i + 1) % count);
        setProgress(0);
        startRef.current = null;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
    };
  }, [reduced, paused, count, index]);

  const pauseHandlers = reduced
    ? {}
    : {
        onMouseEnter: () => setPaused(true),
        onMouseLeave: () => setPaused(false),
        onFocus: () => setPaused(true),
        onBlur: () => setPaused(false),
        onTouchStart: () => setPaused(true),
        onTouchEnd: () => setPaused(false),
      };

  return (
    <div
      className="relative bg-white rounded-3xl border border-slate-100 shadow-lift p-6 sm:p-8 lg:p-9"
      data-testid="testimonial-carousel"
      {...pauseHandlers}
    >
      <div
        className="relative min-h-[520px] sm:min-h-[540px] lg:min-h-[580px]"
        data-testid="testimonial-stage"
      >
        {list.map((t, i) => (
          <TestimonialSlide
            key={t.id || i}
            t={t}
            active={i === index}
            reduced={reduced}
          />
        ))}
      </div>

      <div className="mt-7 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2" role="tablist" aria-label="Testimonials">
          {list.map((t, i) => (
            <button
              key={t.id || i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Show testimonial from ${t.name}`}
              onClick={() => go(i)}
              className={`h-2.5 rounded-full transition-all duration-300 min-w-[12px] ${
                i === index
                  ? "bg-brand w-8"
                  : "bg-slate-300 w-2.5 hover:bg-slate-400"
              }`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
        <p className="text-xs text-slate-400" data-testid="testimonial-counter">
          {index + 1} / {count}
        </p>
      </div>

      {!reduced && count > 1 && (
        <div
          aria-hidden="true"
          className="mt-3 h-1 rounded-full bg-slate-100 overflow-hidden"
          data-testid="testimonial-progress"
        >
          <div
            className="h-full bg-brand/80 transition-[width] duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

function TestimonialSlide({ t, active, reduced }) {
  const starArr = useMemo(
    () => Array.from({ length: t.rating || 5 }),
    [t.rating],
  );
  const objectPosition = t.object_position || "center";
  return (
    <figure
      className={`absolute inset-0 flex flex-col transition-[opacity,transform] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        active
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none"
      } ${reduced ? "transition-none" : ""}`}
      aria-hidden={!active}
      data-testid="testimonial-slide"
      data-active={active}
      data-photo-pending={t.photo_pending ? "true" : "false"}
    >
      {/* Photo: full-width landscape on top — same aspect at every viewport so
          faces + torsos + setting all stay in frame. */}
      <div
        className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden shadow-soft bg-slate-100"
        data-testid="testimonial-photo-wrap"
      >
        {t.photo_url ? (
          <img
            src={t.photo_url}
            alt={
              t.photo_pending
                ? "Customer photo coming soon"
                : `${t.name} from ${t.town} with Silky Smooth Trash Bin Service`
            }
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition }}
            draggable={false}
            data-testid="testimonial-photo-img"
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <Camera size={32} />
          </div>
        )}
        {t.photo_pending && (
          <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-slate-700 text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shadow-soft"
            data-testid="testimonial-photo-pending-pill"
          >
            <Camera size={10} /> Photo coming soon
          </span>
        )}
      </div>

      {/* Quote + caption block */}
      <div className="flex-1 flex flex-col pt-6 sm:pt-7" data-testid="testimonial-quote-wrap">
        <div className="flex items-center gap-1 text-gold mb-3">
          {starArr.map((_, i) => (
            <Star
              key={i}
              size={18}
              fill="currentColor"
              stroke="none"
              className={active && !reduced ? "animate-star-pop" : ""}
              style={
                active && !reduced ? { animationDelay: `${i * 60}ms` } : undefined
              }
            />
          ))}
        </div>
        <blockquote className="font-heading text-lg sm:text-xl lg:text-[22px] font-semibold leading-snug text-ink text-balance mb-5">
          &ldquo;{t.quote}&rdquo;
        </blockquote>
        <figcaption className="flex items-center gap-3 mt-auto">
          <span
            aria-hidden="true"
            className="inline-flex h-10 w-10 rounded-full items-center justify-center text-white font-bold text-sm shadow-soft"
            style={{ backgroundColor: t.avatar_color || "#0EA5E9" }}
          >
            {t.name
              .split(/[\s&]+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((p) => p[0])
              .join("")}
          </span>
          <div>
            <p className="font-semibold text-ink text-base leading-none">
              {t.name}
            </p>
            <p className="text-xs text-slate-500 mt-1 uppercase tracking-[0.18em]">
              {t.town}
            </p>
          </div>
        </figcaption>
      </div>

      <style>{`
        @keyframes star-pop {
          0% { transform: scale(0.7); opacity: 0.5; }
          60% { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-star-pop {
          animation: star-pop 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
      `}</style>
    </figure>
  );
}

export function TestimonialCarouselHeader() {
  return (
    <div>
      <p className="overline mb-3">Neighbors Say</p>
      <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-ink text-balance">
        Real homes. <span className="text-brand">Fresh bins.</span>
      </h2>
      <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-md">
        Straight from driveways in Pine Hill and nearby South Jersey communities.
      </p>
      <Link
        to="/reviews"
        className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-brand transition-colors"
        data-testid="reviews-see-all"
      >
        See all reviews <ArrowRight size={16} />
      </Link>
    </div>
  );
}
